/**
 * @file jobStore.ts
 *
 * @description Persisted registry of the user's report-generation jobs.
 * This store owns WHICH jobs this device tracks (and their last known
 * snapshot); it does NOT poll. Status transport lives in react-query —
 * useActiveJobStatusSync (mounted once via useJobMonitorState) fans a
 * jobStatusQuery out per active job and folds snapshots back in through
 * the pure applyJobStatusSnapshot reducer.
 *
 * The store is persisted to localStorage to maintain job state across
 * browser sessions, which is essential for long-running processing tasks.
 *
 * @module Store
 * @requires zustand
 */
import { create } from "zustand";
import { persist, createJSONStorage } from "zustand/middleware";
import { ReportJobStatus } from "../shared/interfaces/JobStatus";
import { queryClient } from "../shared/api/queryClient";
import { jobKeys } from "../features/jobs/api/jobQueries";
import { retryJob } from "../features/jobs/api/jobStatusApi";
import { applyJobStatusSnapshot } from "../features/jobs/model/applyJobStatus";
import type { JobStatusSnapshot } from "../features/jobs/api/jobStatusApi";

// Represents a video processing job
export interface Job {
  id: string; // Unique identifier for the job
  status: ReportJobStatus; // Current status of the job
  progress: number; // Progress percentage (0-100)
  reportName?: string; // Name of generated report when created
  details?: string; // Additional details information about the job
  error?: string; // Error message if job failed
  evidenceIDs: string[]; // Array of evidenceIDs being processed by this job
  createdAt: Date; // When the job was created
  displayStartedAt?: Date; // When the current user-visible activity cycle started
  selectedFields?: string[]; // Specific report fields selected for extraction
  videoInputLanguage?: string; // Language of the input video(s)
  progressMessage?: string; // Current status message indicating processing stage
  reportId?: string; // ID of the report created in the RMS
  sparseInfo?: Record<string, unknown>; // Additional metadata about the processed evidence
  videoTitles?: string[]; // Title of the video being processed
}

interface JobState {
  jobs: Job[];
  /** Registers a new job. The status-sync fan-out picks it up reactively —
   *  there is no polling to start. */
  addJob: (job: Omit<Job, "createdAt" | "displayStartedAt">) => void;
  removeJob: (id: string) => void;
  /** Folds a /jobStatus snapshot into the registry (pure reducer; no-op —
   *  including no re-render — when nothing changed). */
  applyStatusSnapshot: (id: string, snapshot: JobStatusSnapshot) => void;
  /** Marks a job failed with the given message (hard HTTP errors). */
  failJob: (id: string, error: string) => void;
  /** AWAITING_INPUT → PENDING after the human responded (HITL approval).
   *  Accepts the jobId or the reportId (plan-review threads use either)
   *  and nudges the job's status query for an immediate tick. */
  markJobResumed: (idOrReportId: string) => void;
  /** Retries a failed job under the same jobId. */
  retrySingleJob: (job: Job) => Promise<void>;
  activeJobsCount: () => number;
  isMaxJobsReached: () => boolean;
  MAX_CONCURRENT_JOBS: number;
  /** Logout/timeout: clears the registry and drops all job-status queries. */
  clearStore: () => void;
}

export const useJobStore = create<JobState>()(
  persist(
    (set, get) => ({
      jobs: [],
      MAX_CONCURRENT_JOBS: 3,

      addJob: (job) => {
        const now = new Date();
        set((state) => ({
          jobs: [
            { ...job, createdAt: now, displayStartedAt: now },
            ...state.jobs,
          ],
        }));
      },

      removeJob: (id) => {
        queryClient.removeQueries({ queryKey: jobKeys.status(id) });
        set((state) => ({
          jobs: state.jobs.filter((job) => job.id !== id),
        }));
      },

      applyStatusSnapshot: (id, snapshot) => {
        const { jobs } = get();
        let changed = false;
        const next = jobs.map((job) => {
          if (job.id !== id) return job;
          const updated = applyJobStatusSnapshot(job, snapshot);
          if (updated !== job) changed = true;
          return updated;
        });
        if (changed) set({ jobs: next });
      },

      failJob: (id, error) => {
        const { jobs } = get();
        const job = jobs.find((j) => j.id === id);
        if (!job || (job.status === ReportJobStatus.FAILED && job.error === error)) {
          return;
        }
        set((state) => ({
          jobs: state.jobs.map((j) =>
            j.id === id ? { ...j, status: ReportJobStatus.FAILED, error } : j,
          ),
        }));
      },

      markJobResumed: (idOrReportId) => {
        const job = get().jobs.find(
          (j) => j.id === idOrReportId || j.reportId === idOrReportId,
        );
        if (!job) return;

        set((state) => ({
          jobs: state.jobs.map((j) =>
            j.id === job.id
              ? {
                  ...j,
                  status: ReportJobStatus.PENDING,
                  error: undefined,
                  // Resuming after human input starts a fresh activity cycle.
                  displayStartedAt:
                    j.status === ReportJobStatus.AWAITING_INPUT
                      ? new Date()
                      : j.displayStartedAt,
                }
              : j,
          ),
        }));
        // RESET (not invalidate): the cache still holds the AWAITING_INPUT
        // snapshot, and the fan-out effect would re-apply it over the
        // optimistic PENDING before the refetch lands. resetQueries clears
        // the stale data AND refetches active observers in one call, so the
        // poll predicate resumes off fresh server state.
        void queryClient.resetQueries({ queryKey: jobKeys.status(job.id) });
      },

      retrySingleJob: async (job) => {
        try {
          await retryJob(job.id);

          set((state) => ({
            jobs: state.jobs.map((j) =>
              j.id === job.id
                ? {
                    ...j,
                    status: ReportJobStatus.PENDING,
                    progress: 0,
                    error: undefined,
                    displayStartedAt: new Date(),
                  }
                : j,
            ),
          }));
          // RESET (not invalidate): the cached FAILED snapshot would be
          // re-applied by the fan-out effect the moment the now-PENDING job
          // re-enters the active set — instantly re-failing the retry before
          // the refetch resolves. Reset clears the stale snapshot and still
          // refetches active observers.
          void queryClient.resetQueries({
            queryKey: jobKeys.status(job.id),
          });
        } catch (error) {
          // A store write (not an in-place mutation) so the retry failure
          // actually re-renders and persists.
          get().failJob(
            job.id,
            (error as { response?: { data?: { error?: string } } }).response
              ?.data?.error || "Failed to retry job.",
          );
          console.error("Error retrying job:", error);
        }
      },

      activeJobsCount: () => {
        return get().jobs.filter(
          (job) =>
            job.status === ReportJobStatus.PENDING ||
            job.status === ReportJobStatus.AWAITING_INPUT,
        ).length;
      },

      isMaxJobsReached: () => {
        return get().activeJobsCount() >= get().MAX_CONCURRENT_JOBS;
      },

      clearStore: () => {
        queryClient.removeQueries({ queryKey: jobKeys.all });
        set({ jobs: [] });
      },
    }),
    {
      name: "job-storage", // unique name for localStorage key
      storage: createJSONStorage(() => localStorage),
      // Only persist the jobs array (same shape as every prior release —
      // rehydrated PENDING/AWAITING_INPUT entries re-enter the query
      // fan-out automatically, so reload-resume needs no imperative kick).
      partialize: (state) => ({ jobs: state.jobs }),
    },
  ),
);
